import { Formik } from 'formik';
import _ from 'lodash';
import { AuthSignUpPayload } from 'modules/auth/domain/interfaces/auth-sign-up-payload';
import { authSignUpValidator } from 'modules/auth/domain/validators/auth-sign-up.validator';
import { authSignIn, authSignUp } from 'modules/auth/store/actions';
import {
  authStoreCheckingSelector,
  authStoreCodeRequestedSelector,
  authStoreCodeRequestingSelector,
  authStoreErrorsSelector,
  authStoreLoadingSelector,
} from 'modules/auth/store/selectors';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import { AuthSignInPayload } from '../../../domain/interfaces/auth-sign-in-payload';
import { authSignInValidator } from '../../../domain/validators/auth-sign-in.validator';
import SignInForm from '../../components/sign-in-form';
import AuthLayoutsMain from '../../layouts';

type SportTypeOption = SelectOption<SportType>;

type SignUpFormValues = {
  firstName: string;
  lastName: string;
  middleName?: string;
  phone: string;
  sportType: SportTypeOption | null;
  tournamentName: string;
};

const AuthPagesSignUp = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isSignInLoading = useSelector(authStoreLoadingSelector);
  const stateErrors = useSelector(authStoreErrorsSelector);
  const isAuthCodeRequested = useSelector(authStoreCodeRequestedSelector);
  const isAuthCodeRequesting = useSelector(authStoreCodeRequestingSelector);
  const isAuthChecking = useSelector(authStoreCheckingSelector);

  const [username, setUsername] = useState('');

  const sportTypeOptions = useMemo<SportTypeOption[]>(
    () =>
      Object.values(SportType).map((value) => ({
        value,
        label: _.capitalize(t(`sportType.${value}`)),
      })),
    [t],
  );

  const errors = useMemo(() => {
    if (!stateErrors) {
      return {};
    }

    return Object.keys(stateErrors as { [key: string]: string }).reduce<Record<string, string>>(
      (result, key) => ({
        ...result,
        [key]: t(`signUp.errors.${key}.${stateErrors[key]}`),
      }),
      {},
    );
  }, [stateErrors, t]);

  const onSignUp = useCallback(
    (values: SignUpFormValues) => {
      if (!values.sportType) {
        return;
      }
      const payload: AuthSignUpPayload = {
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName,
        phone: values.phone,
        sportType: values.sportType.value,
        tournamentName: values.tournamentName,
      };
      setUsername(payload.phone);
      dispatch(authSignUp(payload))
        .unwrap()
        .catch((rejected) => {
          toast.error(rejected?.message ?? t('signUp.errors.requestFailed'));
        });
    },
    [dispatch, t],
  );

  const onSignIn = useCallback(
    (data: AuthSignInPayload) => {
      dispatch(authSignIn(data));
    },
    [dispatch],
  );

  return (
    <AuthLayoutsMain
      showPrivacyPolicy
      formContainerProps={{ style: { padding: 30 } }}
      title={t('signUp.title')}
      action={{
        title: t('signUp.signInHint'),
        linkLabel: t('signUp.signInButtonLabel'),
        link: Pages.SIGN_IN,
      }}
    >
      {isAuthCodeRequested || isSignInLoading || isAuthChecking ? (
        <SignInForm
          initialValues={{ username, password: '' }}
          onSubmit={onSignIn}
          isLoading={isSignInLoading || isAuthChecking}
          validationSchema={authSignInValidator(t)}
        />
      ) : (
        <Formik<SignUpFormValues>
          enableReinitialize={true}
          initialValues={{
            firstName: '',
            lastName: '',
            middleName: '',
            phone: '',
            sportType: null,
            tournamentName: '',
          }}
          initialErrors={errors}
          onSubmit={onSignUp}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={authSignUpValidator(t)}
        >
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit}>
              <UDFormInput
                name="lastName"
                placeholder={t('signUp.lastNameLabelPlaceholder')}
                containerProps={{ style: { marginBottom: 12 } }}
                errorVisible={false}
              />
              <UDFormInput
                name="firstName"
                placeholder={t('signUp.firstNameLabelPlaceholder')}
                containerProps={{ style: { marginBottom: 12 } }}
                errorVisible={false}
              />
              <UDFormInput
                name="middleName"
                placeholder={t('signUp.middleNameLabelPlaceholder')}
                containerProps={{ style: { marginBottom: 12 } }}
                errorVisible={false}
              />
              <UDInputPhone
                name="phone"
                placeholder={t('signUp.phoneInputPlaceholder')}
                containerProps={{ style: { marginBottom: 12 } }}
                value={formProps.values.phone}
                errorVisible={false}
              />
              <UDFormSelect
                name="sportType"
                placeholder={t('signUp.sportTypePlaceholder')}
                isMulti={false}
                options={sportTypeOptions}
                containerProps={{ style: { marginBottom: 12 } }}
              />
              <UDFormInput
                name="tournamentName"
                placeholder={t('signUp.tournamentNamePlaceholder')}
                containerProps={{ style: { marginBottom: 18 } }}
                errorVisible={false}
              />
              <UDButton
                variant="primary"
                type="submit"
                className="w-full"
                disabled={!formProps.isValid || !formProps.dirty}
                loading={isAuthCodeRequesting}
              >
                {t('signUp.signUnButtonLabel')}
              </UDButton>
            </form>
          )}
        </Formik>
      )}
    </AuthLayoutsMain>
  );
};

export default AuthPagesSignUp;
